import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, unwrap } from './apiClient'
import { HAS_AUTH } from './auth'
import type {
  ActivityItem,
  AdsReport,
  Briefing,
  BriefingTopic,
  ConnectionStatus,
  MetricsResponse,
  Task,
  User,
} from '@/types'

export function useCurrentUser() {
  return useQuery<User>({
    queryKey: ['user', 'me'],
    queryFn: () => unwrap<User>(apiClient.get('/users/me')),
    retry: 1,
    enabled: HAS_AUTH,
  })
}

export function useAdsReport(range: 'last_7d' | 'last_30d' = 'last_7d') {
  return useQuery<AdsReport>({
    queryKey: ['ads', 'report', range],
    queryFn: () =>
      unwrap<AdsReport>(apiClient.get('/ads/report', { params: { range } })),
    refetchInterval: 300_000,
    enabled: HAS_AUTH,
  })
}

export function useMetrics() {
  return useQuery<MetricsResponse>({
    queryKey: ['metrics'],
    queryFn: () => unwrap<MetricsResponse>(apiClient.get('/metrics')),
    refetchInterval: 600_000,
    enabled: HAS_AUTH,
  })
}

export function useActivity(params?: { q?: string; type?: ActivityItem['type'] | '' }) {
  const q = params?.q?.trim() ?? ''
  // `params?.type` is `ActivityItem['type'] | '' | undefined`; the truthy
  // check already filters '' and undefined, so the explicit `!== ''` was
  // redundant and TS flagged it.
  const type = params?.type ? params.type : undefined
  return useQuery<{ items: ActivityItem[]; totalMatched?: number }>({
    queryKey: ['activity', q, type ?? ''],
    queryFn: () =>
      unwrap<{ items: ActivityItem[]; totalMatched?: number }>(
        apiClient.get('/activity', { params: { q: q || undefined, type } }),
      ),
    refetchInterval: 30_000,
    enabled: HAS_AUTH,
  })
}

export function useAdsTrend(range: 'last_7d' | 'last_30d' = 'last_30d') {
  return useQuery<{ range: string; series: Array<{ date: string; spend: number; roas: number; leads: number }> }>({
    queryKey: ['ads', 'trend', range],
    queryFn: () =>
      unwrap(apiClient.get('/ads/trend', { params: { range } })),
    refetchInterval: 600_000,
    enabled: HAS_AUTH,
  })
}

export function useConnections() {
  return useQuery<{ items: ConnectionStatus[] }>({
    queryKey: ['connections'],
    queryFn: () => unwrap<{ items: ConnectionStatus[] }>(apiClient.get('/setup/connections')),
    refetchInterval: 60_000,
    enabled: HAS_AUTH,
  })
}

export function useTasks(status?: Task['status']) {
  return useQuery<{ items: Task[] }>({
    queryKey: ['tasks', status],
    queryFn: () =>
      unwrap<{ items: Task[] }>(
        apiClient.get('/tasks', { params: status ? { status } : undefined }),
      ),
    refetchInterval: 60_000,
    enabled: HAS_AUTH,
  })
}

export function useTodayBriefing() {
  return useQuery<{ briefing: Briefing | null }>({
    queryKey: ['briefing', 'today'],
    queryFn: () =>
      unwrap<{ briefing: Briefing | null }>(apiClient.get('/briefing/today')),
    refetchInterval: 600_000,
    enabled: HAS_AUTH,
  })
}

export function useSuggestedBriefingTopics() {
  return useMutation<BriefingTopic[]>({
    mutationFn: async () => {
      const data = await unwrap<{ topics: BriefingTopic[] }>(
        apiClient.post('/briefing/suggest', {}),
      )
      return data.topics
    },
  })
}

// =============================================================================
// Usage, limits & billing — see docs/48. AI credits + voice minutes meters,
// PAYG/master toggles, and prepaid pack purchase.
// =============================================================================

export interface AiUsage {
  period: string
  usedCredits: number
  includedCredits: number
  topupCredits: number
  remainingCredits: number
  tier: string
  aiEnabled: boolean
  overageOptIn: boolean
  overageUsdPerCredit: number
  byFeature: Record<string, { credits: number; costUsd: number }>
  costUsd: number
}

export function useAiUsage() {
  return useQuery<AiUsage>({
    queryKey: ['ai', 'usage'],
    queryFn: () => unwrap<AiUsage>(apiClient.get('/ai/usage')),
    refetchInterval: 120_000,
    enabled: HAS_AUTH,
  })
}

export interface VoiceUsage {
  used: number
  included: number
  topup: number
  remaining: number
  tier: string
  voiceEnabled: boolean
  overageOptIn: boolean
  dailyCallLimit: number
}

export function useVoiceUsage() {
  return useQuery<VoiceUsage>({
    queryKey: ['voice', 'usage'],
    queryFn: () => unwrap<VoiceUsage>(apiClient.get('/voice/usage')),
    refetchInterval: 120_000,
    enabled: HAS_AUTH,
  })
}

// =============================================================================
// User learning / memory — "What Jarvis knows about you" (doc 55 increment 3)
// =============================================================================

export interface ProfileMemoryItem {
  id: string
  text: string
  facet: 'goal' | 'concern' | 'delivery_pref' | 'comms_style' | 'fact'
  source: string
  confidence: 'low' | 'medium' | 'high'
  status: 'active' | 'archived'
}

export interface ProfileMoneyMove {
  id: string
  title: string
  rationale: string
  estimatedValue?: string
  basis: string[]
  status: 'suggested' | 'accepted' | 'dismissed' | 'done'
  createdAt: string
}

export interface LearnedProfile {
  deliveryPreferences: string
  commsStyle: string
  goals: ProfileMemoryItem[]
  recurringConcerns: ProfileMemoryItem[]
  facts: ProfileMemoryItem[]
  moneyMoves: ProfileMoneyMove[]
  pendingCount: number
  updatedAt: string
}

export function useProfile() {
  return useQuery<LearnedProfile>({
    queryKey: ['profile'],
    queryFn: async () =>
      (await unwrap<{ profile: LearnedProfile }>(apiClient.get('/profile'))).profile,
    enabled: HAS_AUTH,
  })
}

export interface ProfilePatch {
  deliveryPreferences?: string
  commsStyle?: string
  editItem?: { id: string; text: string }
  archiveItemId?: string
  moneyMove?: { id: string; status: 'accepted' | 'dismissed' | 'done' }
}

export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: ProfilePatch) => unwrap(apiClient.patch('/profile', patch)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  })
}

export type PackId =
  | 'credits_1k'
  | 'credits_5k'
  | 'credits_20k'
  | 'minutes_100'
  | 'minutes_500'
  | 'minutes_1000'

/** Start a one-time Stripe checkout for a prepaid pack and redirect to it. */
export function useBuyPack() {
  return useMutation<void, Error, PackId>({
    mutationFn: async (packId) => {
      const { url } = await unwrap<{ url: string }>(
        apiClient.post('/billing/buy-pack', { packId }),
      )
      if (url) window.location.href = url
    },
  })
}

/** PATCH the usage master switches / PAYG opt-ins (top-level User flags). */
export function useUpdateAccountFlags() {
  const qc = useQueryClient()
  return useMutation<
    void,
    Error,
    Partial<{
      aiEnabled: boolean
      aiOverageOptIn: boolean
      voiceEnabled: boolean
      minuteOverageOptIn: boolean
    }>
  >({
    mutationFn: async (flags) => {
      await unwrap(apiClient.patch('/settings', flags))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai', 'usage'] })
      qc.invalidateQueries({ queryKey: ['voice', 'usage'] })
    },
  })
}
